import {
	CSSProperties,
	ChangeEvent,
	FC,
	InputHTMLAttributes,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import classNames from 'classnames';

import useDeviceType from '../../../providers/DeviceType/hooks';
import Button from '../Button';
import LoadingSpinner from '../LoadingSpinner';
import RemoveIcon from '../icons/Remove';
import SearchIcon from '../icons/Search';

import styles from './styles.module.scss';

type InputFieldType = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> & {
	size?: 'small' | 'default' | 'large';
	style?: CSSProperties;
	className?: string;
	disabled?: boolean;
	loading?: boolean;
	withShadow?: boolean;
	searchable?: boolean;
	withClearButton?: boolean;
	value?: string;
	error?: string;
	label?: string | JSX.Element;
	onChangeText?: (text?: string) => void;
};

const InputField: FC<InputFieldType> = ({
	size,
	style,
	className,
	disabled,
	loading,
	withShadow = true,
	searchable,
	onChangeText,
	value,
	label,
	error,
	type = 'text',
	withClearButton,
	...props
}) => {
	const inputRef = useRef<any>(null);
	const { isMobile, isDesktop } = useDeviceType();
	const [showClearButton, setShowClearButton] = useState(
		!!value?.length && (searchable || withClearButton)
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			const value = event.currentTarget.value;
			setShowClearButton(value.length > 0);

			if (disabled) return;
			onChangeText?.(value);
			props.onChange?.(event);
		},
		[disabled, props.onChange]
	);

	const handleClearButton = useCallback(() => {
		if (disabled) return;

		if (inputRef.current) {
			setShowClearButton(false);
			onChangeText?.('');
			inputRef.current.focus();
		}
	}, []);

	useEffect(() => {
		if (error) {
			inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
		}
	}, [inputRef, error]);

	return (
		<>
			{typeof label === 'string' && <div className={styles.inputFieldLabel}>{label}</div>}
			{typeof label === 'object' && label}
			<div className={classNames(styles.inputField)}>
				<input
					ref={inputRef}
					value={value || ''}
					onChange={handleChange}
					style={style}
					className={classNames([
						size === 'small' && styles.sizeSmall,
						size === 'large' && styles.sizeLarge,
						withShadow && styles.withShadow,
						disabled && styles.disabled,
						loading && styles.loading,
						isDesktop && styles.isDesktop,
						isMobile && styles.isMobile,
						error && styles.withError,
						searchable && styles.searchable,
						className,
					])}
					type={type}
					{...props}
				/>
				{searchable && !showClearButton && !loading && (
					<div className={styles.iconSearchHolder}>
						<SearchIcon />
					</div>
				)}
				{(searchable || withClearButton) && showClearButton && !loading && (
					<Button
						withShadow={false}
						appearance={'primary'}
						onClick={handleClearButton}
						className={styles.iconSearchClear}>
						<RemoveIcon size={20} />
					</Button>
				)}
				{loading && (
					<LoadingSpinner size={size === 'small' ? 16 : 24} className={styles.loadingSpinner} />
				)}
				{error && <div className={styles.error}>{error}</div>}
			</div>
		</>
	);
};

export default InputField;
